import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ServiceOrderState } from '@horology/shared-types';
import { listServiceOrders } from './os.api';
import { STATE_COLORS, STATE_LABELS, STATE_ORDER } from './state-machine';
import { formatCop } from '../../lib/format';
import { hasPermission, useMe } from '../../hooks/useAuth';

export function OrdersListPage() {
  const me = useMe();
  const canCreate = hasPermission(me.data, 'os.create');
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [state, setState] = useState<ServiceOrderState | ''>('');

  const orders = useQuery({
    queryKey: ['service-orders', { q, state }],
    queryFn: () =>
      listServiceOrders({
        q: q || undefined,
        state: state || undefined,
        limit: 100,
      }),
  });

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0 }}>Órdenes de Servicio</h1>
        {canCreate && (
          <button className="btn btn-primary" onClick={() => navigate({ to: '/orders/new' })}>
            + Nueva OS
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          className="input"
          placeholder="Buscar por folio, cliente, marca, serial..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <select
          className="input"
          style={{ width: 180 }}
          value={state}
          onChange={(e) => setState(e.target.value as ServiceOrderState | '')}
        >
          <option value="">Todos los estados</option>
          {STATE_ORDER.map((s) => (
            <option key={s} value={s}>
              {STATE_LABELS[s]}
            </option>
          ))}
          <option value="CANCELLED">{STATE_LABELS.CANCELLED}</option>
          <option value="ABANDONED">{STATE_LABELS.ABANDONED}</option>
        </select>
      </div>

      {orders.isLoading && <p>Cargando...</p>}
      {orders.isError && <p className="error">No se pudieron cargar las órdenes.</p>}

      {orders.data && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 1rem' }}>Folio</th>
                <th style={{ padding: '0.75rem 1rem' }}>Estado</th>
                <th style={{ padding: '0.75rem 1rem' }}>Cliente</th>
                <th style={{ padding: '0.75rem 1rem' }}>Reloj</th>
                <th style={{ padding: '0.75rem 1rem' }}>Ingreso</th>
                <th style={{ padding: '0.75rem 1rem' }}>Estimado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders.data.data.map((o) => (
                <tr key={o.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <Link to="/orders/$id" params={{ id: String(o.id) }}>
                      <code>{o.folio}</code>
                    </Link>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span
                      style={{
                        background: STATE_COLORS[o.state],
                        color: '#fff',
                        padding: '2px 10px',
                        borderRadius: 999,
                        fontSize: '0.75rem',
                      }}
                    >
                      {STATE_LABELS[o.state]}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {o.customer.firstName} {o.customer.lastName}
                    <br />
                    <small style={{ color: 'var(--color-muted)' }}>{o.customer.idDocument}</small>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {o.brand} {o.model}
                    {o.serial && (
                      <>
                        <br />
                        <small style={{ color: 'var(--color-muted)' }}>S/N {o.serial}</small>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.85rem' }}>
                    {new Date(o.intakeAt).toLocaleDateString('es-CO')}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>{formatCop(o.estimatedValue)}</td>
                  <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                    <Link
                      to="/orders/$id"
                      params={{ id: String(o.id) }}
                      className="btn"
                    >
                      Abrir
                    </Link>
                  </td>
                </tr>
              ))}
              {orders.data.data.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>
                    No hay órdenes que coincidan con el filtro.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
